import { useMFEContainer } from '@qbe/dfc-mfe-components';
import { MFEEventPayload } from '@qbe/dfc-micro-frontends';
import { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { config } from '~/config';
import { CYBERSOURCE_MERCHANT_ID, RETRY_MESSAGE } from '~/constants/domain-specific';
import { PaymentErrorCode } from '~/types/payments';

import {
  ALLOWED_CARDS,
  IFRAME_CONTAINER_ID,
  IFRAME_HEIGHT,
  IFRAME_LOADING_TIMEOUT,
  MessageResultType,
  MessageType,
  MFE_NAME,
  MFE_TITLE,
  PAYMENT_TYPE_CREDIT_CARD,
  SOURCE_SYSTEM,
} from './constants';
import { PaymentFormData, PaymentFormState, UsePaymentMFEResult } from './types';

export const usePaymentMFE = (): UsePaymentMFEResult => {
  const [formState, setFormState] = useState<PaymentFormState>({
    isReady: false,
    isValid: false,
    isExpired: false,
    hasError: false,
  });
  const [formData, setFormData] = useState<PaymentFormData>({
    transientToken: '',
    cardHolderName: '',
    cardType: '',
  });

  const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const submissionRef = useRef<boolean>(false);
  const [sessionId] = useState<string>(() => uuidv4());
  const { digitalPaymentUrl } = config;

  const iframeAttributes = {
    height: IFRAME_HEIGHT,
    width: '100%',
  };

  const contextData = useMemo(
    () => ({
      api: {
        headers: {
          sourceSystem: SOURCE_SYSTEM,
          sessionId,
        },
      },
      payment: {
        paymentType: PAYMENT_TYPE_CREDIT_CARD,
        allowedCards: ALLOWED_CARDS,
        merchantIdentifier: CYBERSOURCE_MERCHANT_ID,
      },
    }),
    [sessionId]
  );

  const handleError = (errorMessage: string, errorCode?: string) => {
    setFormState((prev) => ({
      ...prev,
      hasError: true,
      errorCode,
      errorMessage,
    }));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleMFEInit = (result: string, rest: any) => {
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
      initTimeoutRef.current = null;
    }
    if (result === MessageResultType.FAILED) {
      handleError(`Payment MFE init failed: ${rest.error?.message}`);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleCCFormInit = (result: string, rest: any) => {
    if (result === MessageResultType.SUCCESS) {
      setFormState((prev) => ({
        ...prev,
        isReady: true,
      }));
    } else if (result === MessageResultType.FAILED) {
      handleError(`Payment MFE credit card form init failed: ${rest.error?.message}`);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePaymentResponse = (result: string, rest: any) => {
    if (submissionRef.current) {
      return;
    }
    if (result === MessageResultType.SUCCESS) {
      submissionRef.current = true;
      const {
        transientToken,
        creditCardInfo: { cardHolderName, cardType },
      } = rest;
      setFormData({
        transientToken,
        cardHolderName,
        cardType,
      });
    } else if (result === MessageResultType.FAILED) {
      submissionRef.current = false;
      setFormState((prev) => ({
        ...prev,
        hasError: true,
        errorCode: PaymentErrorCode.PAYMENT_ERROR_RETRY,
        errorMessage: RETRY_MESSAGE,
      }));
    }
  };

  const onReceiveMessage = ({ type, result = '', ...rest }: MFEEventPayload) => {
    switch (type) {
      case MessageType.MFE_INIT:
        handleMFEInit(result as string, rest);
        break;
      case MessageType.CC_FORM_INIT:
        handleCCFormInit(result as string, rest);
        break;
      case MessageType.CC_FORM_VALID:
        setFormState((prev) => ({
          ...prev,
          isValid: result === MessageResultType.SUCCESS,
        }));
        break;
      case MessageType.PAYMENT_STATUS:
        handlePaymentResponse(result as string, rest);
        break;
      case MessageType.CC_FORM_EXPIRED:
        setFormState((prev) => ({
          ...prev,
          isExpired: true,
        }));
        break;
      default:
        // ignore other messages
        break;
    }
  };

  const { mfeInstanceRef, isLoading } = useMFEContainer({
    name: MFE_NAME,
    title: MFE_TITLE,
    url: digitalPaymentUrl,
    selector: `#${IFRAME_CONTAINER_ID}`,
    contextData,
    iframeAttributes,
    onMessage: onReceiveMessage,
  });

  useEffect(() => {
    // Set timeout to check for MFE_INIT message
    initTimeoutRef.current = setTimeout(() => {
      handleError('Payment MFE init timed out');
      initTimeoutRef.current = null;
    }, IFRAME_LOADING_TIMEOUT);

    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
    };
  }, []);

  return { mfeInstanceRef, isLoading, formState, formData };
};
